import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { DatePipe, NgOptimizedImage, SlicePipe } from '@angular/common';

import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatOptionModule, MatRipple } from '@angular/material/core';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';

import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { Trip } from 'src/app/shared/classes/trip';
import { ESvgIcons } from 'src/app/core/enums/svg-icons';
import { NIGHTS } from 'src/app/shared/constants/nights-array.const';
import { PIN_COLORS } from 'src/app/shared/constants/map-pin.constant';
import { ETripPlannerView } from 'src/app/core/enums/trip-planner-view.enum';
import {
  IAltDest,
  IParsedTripLeg,
  ITripDestination,
} from 'src/app/core/types/interfaces/trip-elements.interface';

import { SafeHtmlPipe } from 'src/app/shared/pipes/safe-html.pipe';
import { GetFavoritesByProvider } from '../../../../user-account/user-wish-list-page/measure-amount-of-favorites.pipe';

import { AuthService } from 'src/app/core/services/auth.service';
import { SsrHelperService } from 'src/app/core/services/ssr-helper.service';
import { BreakpointService } from 'src/app/core/services/breakpoint.service';
import { GoogleAnalyticService } from 'src/app/core/services/google-analytic.service';
import { TripWizardStateService } from 'src/app/shared/components/trip-wizard/trip-wizard-state.service';

import { DbdBlogDialogComponent } from 'src/app/shared/dialogs/dbd-blog-dialog/dbd-blog-dialog.component';
import { ChangeStayDialogComponent } from '../../dialogs/change-stay-dialog/change-stay-dialog.component';
import { ChangeDestinationDialogComponent } from '../../dialogs/change-destination-dialog/change-destination-dialog.component';
import { RemoveDestinationDialogComponent } from '../../dialogs/remove-destination-dialog/remove-destination-dialog.component';

import moment from 'moment';

@Component({
  selector: 'app-trip-destinations-list',
  imports: [
    MatOptionModule,
    MatSelectModule,
    NgOptimizedImage,
    ReactiveFormsModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    SlicePipe,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatRipple,
    GetFavoritesByProvider,
    NgxSkeletonLoaderModule,
    SafeHtmlPipe,
    DatePipe,
  ],
  templateUrl: './trip-destinations-list.component.html',
  styleUrl: './trip-destinations-list.component.scss',
})
export class TripDestinationsListComponent implements OnInit {
  @Input() packages: any[] = [];
  @Input() destinations: ITripDestination[] = [];
  @Input() isFetching: boolean = false;
  @Input() legs: IParsedTripLeg[] = [];
  @Input() plannerView: ETripPlannerView;
  @Input() isSpecifyDates: boolean = false;
  @Input() isOptimizedMode: boolean = true;
  @Input() cityInfoIndex: number = -1;
  @Output() toggleExpand: EventEmitter<void> = new EventEmitter<void>();
  @Output() onSelectTrip: EventEmitter<number> = new EventEmitter<number>();
  @Output() onLockOrUnlockDestination: EventEmitter<ITripDestination> =
    new EventEmitter<ITripDestination>();
  @Output() onScrollToElement = new EventEmitter<string>();
  @Output() changeDestination: EventEmitter<{
    index: number;
    newDest: IAltDest;
    oldDest: ITripDestination;
  }> = new EventEmitter<{
    index: number;
    newDest: IAltDest;
    oldDest: ITripDestination;
  }>();
  @Output() changeDestinationDuration: EventEmitter<{
    dest: ITripDestination;
    maintain: boolean;
    numDays: number;
    tripEndDate: Date;
  }> = new EventEmitter();
  @Output() removeDestination: EventEmitter<{
    destination: ITripDestination;
    index: number;
  }> = new EventEmitter<{ destination: ITripDestination; index: number }>();
  @Output() toggleDestinationsChanges: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() toggleSightsOnTheWay: EventEmitter<void> = new EventEmitter<void>();

  public trip: WritableSignal<Trip>;
  public generalZoomLevel: number = 1;
  public selectedStopoversBoxIndex: number = -1;
  public breakpointService: BreakpointService = inject(BreakpointService);

  protected readonly PIN_COLORS: string[] = PIN_COLORS;
  protected readonly NIGHTS: { value: number; text: string }[] = NIGHTS;
  protected readonly ESvgIcons = ESvgIcons;
  protected readonly ETripPlannerView = ETripPlannerView;

  #tripWizardStateService: TripWizardStateService = inject(
    TripWizardStateService,
  );
  #matDialog: MatDialog = inject(MatDialog);
  #ssrHelperService: SsrHelperService = inject(SsrHelperService);
  #googleAnalyticService: GoogleAnalyticService = inject(GoogleAnalyticService);
  #authService: AuthService = inject(AuthService);

  ngOnInit(): void {
    this.trip = signal(this.#tripWizardStateService.currentTripStateValue);
    if (this.#ssrHelperService.userAgent) {
      const browser = this.#ssrHelperService.userBrowser.toLowerCase();
      if (browser.includes('safari')) return;
      const zoomLevel = parseFloat(getComputedStyle(document.body).zoom) || 1;

      if (zoomLevel !== 1) {
        this.generalZoomLevel = zoomLevel;
      }
    }
  }

  setNightsString(days: number): string {
    const correctedNights = days === 0 ? 0 : days;
    return correctedNights + (correctedNights === 1 ? ' night' : ' nights');
  }

  drop(event: CdkDragDrop<string[]>): void {
    if (event.currentIndex === event.previousIndex) return;
    this.#tripWizardStateService.byUserInteraction = true;
    this.trip = signal(this.#tripWizardStateService.currentTripStateValue);
    moveItemInArray(this.destinations, event.previousIndex, event.currentIndex);
    const tripRoutes = this.parsedTripRoutes();
    const firstDestination = tripRoutes[0];
    const lastDestination = tripRoutes[tripRoutes.length - 1];
    const getTripName = () => {
      const { startingLocationId, name } = this.trip();
      if (startingLocationId.toString() !== firstDestination.id.toString()) {
        return '';
      }
      return name;
    };

    this.trip.update((trip) =>
      trip.updateTrip({
        startingLocationId: String(firstDestination.id),
        startingLabel: String(firstDestination.name),
        startingCountry: String(firstDestination.countryId),
        endingLocationId: String(lastDestination.id),
        endingLabel: String(lastDestination.name),
        endingCountry: String(lastDestination.countryId),
        isOptimizedMode: false,
        name: getTripName(),
        route: tripRoutes,
      }),
    );
    this.updateTripState();
    this.toggleDestinationsChanges.emit();
    this.#googleAnalyticService.setItineraryInteractionAnalytic({
      itinerary_interactions_type: 'order_change',
      userID: this.#authService.userId,
    });
  }

  selectStopoversBoxIndex(index: number): void {
    if (this.breakpointService.isMobile()) return;

    if (index === this.selectedStopoversBoxIndex) {
      this.selectedStopoversBoxIndex = -1;
      this.toggleSightsOnTheWay.emit();
      return;
    }
    this.selectedStopoversBoxIndex = index;
    this.toggleSightsOnTheWay.emit();
    this.#googleAnalyticService.setItineraryInteractionAnalytic({
      itinerary_interactions_type: 'sights on the way',
      userID: this.#authService.userId,
    });
  }

  hasScroll(element: HTMLElement): boolean {
    return element.scrollHeight > element.clientHeight;
  }

  toggleNightsChange(
    value: number,
    destination: ITripDestination,
    e: MouseEvent,
  ): void {
    e.stopPropagation();
    e.preventDefault();

    const newDurationValue = destination.days - 1 + value;
    if (newDurationValue < 0) return;
    this.onChangeNightsOfDestination(newDurationValue, destination);
  }

  onChangeNightsOfDestination(
    value: number,
    destination: ITripDestination,
    optionSource: null | MatSelect = null,
  ): void {
    this.trip = signal(this.#tripWizardStateService.currentTripStateValue);
    const { isSpecifyDates, numDays, tripEndDate } = this.trip();
    const diffDays: number = value - (destination.days - 1);
    const newTripEndDate = moment(tripEndDate).add(diffDays, 'days');
    const newEndDate = (): string => {
      if (!isSpecifyDates) return '';
      return newTripEndDate.format('DD MMM YYYY');
    };

    this.#matDialog
      .open(ChangeStayDialogComponent, {
        width: '524px',
        maxWidth: '96vw',
        data: {
          newEndDate: newEndDate(),
          diffDays: diffDays,
          numDays: numDays,
          cityName: destination.cityName,
          isOptimizedMode: this.isOptimizedMode,
        },
      })
      .afterClosed()
      .subscribe((body: { res: boolean; maintain: boolean }) => {
        if (optionSource) optionSource.value = destination.days - 1;
        if (!body.res) return;
        destination.days = value + 1;
        const newNumDays: number = numDays + diffDays;
        this.changeDestinationDuration.emit({
          dest: destination,
          numDays: newNumDays,
          maintain: body.maintain,
          tripEndDate: newTripEndDate.toDate(),
        });
        this.#googleAnalyticService.setItineraryInteractionAnalytic({
          itinerary_interactions_type: 'nights_change',
          userID: this.#authService.userId,
        });
      });
  }

  onChangeDestination(destination: ITripDestination, index: number): void {
    const place: string = destination.region
      ? destination.region
      : destination.countryName;
    const message: string = `${destination.cityName}, ${place}`;

    this.#matDialog
      .open(ChangeDestinationDialogComponent, {
        maxWidth: '524px',
        data: {
          name: message,
          index: destination.destIndex,
          altDestinations: destination.altDests,
        },
      })
      .afterClosed()
      .subscribe((newDest: null | IAltDest) => {
        if (!newDest) return;
        this.#tripWizardStateService.byUserInteraction = true;
        this.changeDestination.next({
          index,
          newDest: newDest,
          oldDest: destination,
        });
        this.#googleAnalyticService.setItineraryInteractionAnalytic({
          itinerary_interactions_type: 'change_destanation',
          userID: this.#authService.userId,
        });
      });
  }

  onRemoveDestination(destination: ITripDestination, index: number): void {
    this.trip = signal(this.#tripWizardStateService.currentTripStateValue);
    const { isSpecifyDates, numDays, tripEndDate } = this.trip();
    const newTripEndDate = moment(tripEndDate).subtract(
      destination.days,
      'days',
    );
    const newEndDate = (): string => {
      if (!isSpecifyDates) return '';
      return newTripEndDate.format('DD MMM YYYY');
    };
    this.#matDialog
      .open(RemoveDestinationDialogComponent, {
        width: '524px',
        maxWidth: '96vw',
        data: {
          newEndDate: newEndDate(),
          minusDays: destination.days,
          cityName: destination.cityName,
          isOptimizedMode: this.isOptimizedMode,
        },
      })
      .afterClosed()
      .subscribe((body: { res: boolean; maintain: boolean }) => {
        if (!body.res) return;
        this.#tripWizardStateService.byUserInteraction = true;
        this.#googleAnalyticService.setItineraryInteractionAnalytic({
          itinerary_interactions_type: 'remove_destanation',
          userID: this.#authService.userId,
        });
        if (!body.maintain) {
          const newNumDays: number = numDays - destination.days;
          this.trip.update((trip) =>
            trip.updateTrip({
              numDays: newNumDays,
              tripEndDate: newTripEndDate.toDate(),
            }),
          );
          this.updateTripState();
        }
        this.removeDestination.next({ destination, index });
      });
  }

  onOpenDBDTripBlog(): void {
    const { blogUrl } = this.trip();
    const firstDest = this.destinations[0];
    const lastDest = this.destinations[this.destinations.length - 1];
    this.#matDialog.open(DbdBlogDialogComponent, {
      width: '991px',
      maxWidth: '96vw',
      height: '1500px',
      maxHeight: '96dvh',
      panelClass: 'mobile-container',
      data: {
        tripName: `${firstDest.cityName} to ${lastDest.cityName}`,
        blogUrl: blogUrl,
      },
    });
  }

  handleScrollTo(e: MouseEvent, elementName: string) {
    e.stopPropagation();
    this.onScrollToElement.emit(elementName);
  }

  private parsedTripRoutes() {
    return this.destinations.map((destination: ITripDestination) => ({
      coordinates: destination.coordinates,
      countryId: destination.country_id,
      days: destination.days,
      name: destination.name,
      id: destination.id,
    }));
  }

  private updateTripState(): void {
    this.#tripWizardStateService.currentTripState = new Trip({
      ...this.trip(),
    });
  }
}
